import {
  getCurrentSitePublicId,
  subscribeToCurrentSiteChange,
} from './api';

/**
 * 为不能走共享 axios 实例的请求构造与当前登录会话一致的请求头。
 * 目前仅用于需要直接读取 Response.body 的 SSE；普通 JSON 请求应继续使用 services/api。
 */
export const buildSessionRequestHeaders = (initialHeaders?: HeadersInit): Headers => {
  const headers = new Headers(initialHeaders);
  headers.delete('Authorization');
  headers.delete('X-Site-Id');
  headers.set('X-Requested-With', 'XMLHttpRequest');

  return headers;
};

const SITE_INDEPENDENT_API_PATHS = [
  '/api/auth',
  '/api/platform',
  '/api/health',
  '/api/sites',
];

const isSameOrigin = (url: URL): boolean => (
  typeof window === 'undefined' || url.origin === window.location.origin
);

const rewriteUrlForSite = (url: URL, sitePublicId: string): URL => {
  const isApiPath = url.pathname === '/api' || url.pathname.startsWith('/api/');
  const isSiteIndependent = SITE_INDEPENDENT_API_PATHS.some(
    (prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
  );
  if (!isSameOrigin(url) || !isApiPath || isSiteIndependent) {
    return url;
  }

  const rewritten = new URL(url.toString());
  const businessPath = url.pathname.slice('/api'.length);
  rewritten.pathname = `/api/sites/${encodeURIComponent(sitePublicId)}${businessPath}`;
  return rewritten;
};

export const toCanonicalSiteRequest = (
  input: RequestInfo | URL,
  sitePublicId: string | null = getCurrentSitePublicId(),
): RequestInfo | URL => {
  if (!sitePublicId) {
    return input;
  }

  if (typeof input === 'string') {
    const isRelative = input.startsWith('/');
    const base = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const rewritten = rewriteUrlForSite(new URL(input, base), sitePublicId);
    return isRelative
      ? `${rewritten.pathname}${rewritten.search}${rewritten.hash}`
      : rewritten.toString();
  }

  if (input instanceof URL) {
    return rewriteUrlForSite(input, sitePublicId);
  }

  const rewritten = rewriteUrlForSite(new URL(input.url), sitePublicId);
  return rewritten.toString() === input.url ? input : new Request(rewritten, input);
};

export const sessionFetch = (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
  const sitePublicId = getCurrentSitePublicId();
  return fetch(toCanonicalSiteRequest(input, sitePublicId), {
    ...init,
    credentials: 'same-origin',
    headers: buildSessionRequestHeaders(init.headers),
  });
};

export interface SessionSseEvent {
  type: string;
  data: string;
  lastEventId: string;
}

export interface SessionEventStreamConnection {
  close: () => void;
  done: Promise<void>;
}

interface SessionEventStreamHandlers {
  onEvent: (event: SessionSseEvent) => void;
  onError?: (error: unknown) => void;
}

/**
 * 用 fetch 消费同源 SSE；身份由 HttpOnly Cookie 携带，Site 只来自 canonical path。
 * close() 通过 AbortController 立即取消读取；服务端断流或解析失败时交给 onError。
 */
export const openSessionEventStream = (
  url: string,
  handlers: SessionEventStreamHandlers,
): SessionEventStreamConnection => {
  const controller = new AbortController();
  const streamSitePublicId = getCurrentSitePublicId();
  const unsubscribe = subscribeToCurrentSiteChange((nextSitePublicId) => {
    if (nextSitePublicId !== streamSitePublicId) {
      controller.abort();
    }
  });

  const done = (async () => {
    const response = await sessionFetch(url, {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`SSE request failed with HTTP ${response.status}`);
    }
    if (!response.body) {
      throw new Error('SSE response body is unavailable');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventType = 'message';
    let lastEventId = '';
    let dataLines: string[] = [];

    const dispatch = () => {
      if (dataLines.length > 0) {
        handlers.onEvent({
          type: eventType || 'message',
          data: dataLines.join('\n'),
          lastEventId,
        });
      }
      eventType = 'message';
      dataLines = [];
    };

    const processLine = (line: string) => {
      if (line === '') {
        dispatch();
        return;
      }
      if (line.startsWith(':')) {
        return;
      }

      const separatorIndex = line.indexOf(':');
      const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex);
      let value = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1);
      if (value.startsWith(' ')) value = value.slice(1);

      if (field === 'event') {
        eventType = value;
      } else if (field === 'data') {
        dataLines.push(value);
      } else if (field === 'id' && !value.includes('\0')) {
        lastEventId = value;
      }
    };

    try {
      while (!controller.signal.aborted) {
        const { done: readerDone, value } = await reader.read();
        if (readerDone) break;

        buffer += decoder.decode(value, { stream: true });
        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1 && !controller.signal.aborted) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          processLine(line);
          newlineIndex = buffer.indexOf('\n');
        }
      }

      buffer += decoder.decode();
      if (!controller.signal.aborted && buffer) {
        processLine(buffer.endsWith('\r') ? buffer.slice(0, -1) : buffer);
      }
      if (!controller.signal.aborted) {
        dispatch();
      }
    } finally {
      reader.releaseLock();
    }

    if (!controller.signal.aborted) {
      throw new Error('SSE connection closed');
    }
  })()
    .catch((error) => {
      if (!controller.signal.aborted) {
        handlers.onError?.(error);
      }
    })
    .finally(unsubscribe);

  return {
    close: () => controller.abort(),
    done,
  };
};
